/**
 * Sentry initialisation (frontend)
 * ====================================
 * Env-var driven. If `REACT_APP_SENTRY_DSN` is not set, this is a no-op so
 * dev / preview environments don't pollute the production Sentry project.
 *
 * Strips PII from events before send (auth tokens, emails, IPs).
 *
 * Wire by importing this module from `src/index.js` BEFORE any React render.
 */
import * as Sentry from '@sentry/react';

const SENSITIVE_HEADERS = new Set([
  'authorization', 'x-api-key', 'cookie', 'set-cookie', 'x-cartie-signature',
]);

function scrub(event) {
  try {
    if (event.user) {
      delete event.user.email;
      delete event.user.ip_address;
    }
    const headers = event.request?.headers || {};
    Object.keys(headers).forEach((h) => {
      if (SENSITIVE_HEADERS.has(h.toLowerCase())) headers[h] = '[scrubbed]';
    });
    if (typeof event.transaction === 'string' && event.transaction.includes('?')) {
      event.transaction = event.transaction.split('?')[0];
    }
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map((b) => {
        if (b.category === 'console' && typeof b.message === 'string') {
          b.message = b.message
            .replace(/(token|password|secret)[=:]\s*[^\s,;]+/gi, '$1=***');
        }
        return b;
      });
    }
  } catch (_) { /* never crash because of scrubbing */ }
  return event;
}

function filterBreadcrumb(b) {
  try {
    if (b.category === 'console' && b.level === 'debug') return null;
    const url = b.data?.url || '';
    if (/\/(health|ping|system-status)/.test(url)) return null;
  } catch (_) { /* */ }
  return b;
}

let _initialised = false;
export function initSentry() {
  if (_initialised) return false;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;
  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
      replaysOnErrorSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      sendDefaultPii: false,
      beforeSend: scrub,
      beforeBreadcrumb: filterBreadcrumb,
    });
    _initialised = true;
    return true;
  } catch (e) {
    // never let monitoring break the app
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('Sentry init failed:', e?.message);
    }
    return false;
  }
}

/** Imperative helper for explicit capture from any module. */
export function captureError(err, context = {}) {
  if (!_initialised) return;
  try { Sentry.captureException(err, { extra: context }); } catch (_) { /* */ }
}

export { Sentry };
